/**
 * Prescriptions: received, reviewed by a named pharmacist, dispensed in parts.
 *
 * Three statuses are shown apart because they are apart: dispensing lives on
 * the prescription, payment on the sale it was dispensed through, and
 * delivery on the fulfilment. Nothing clinical is written by CenoX — the
 * directions on a label are the words the reviewing pharmacist typed.
 */

import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, FileText, Paperclip, Plus, Printer, ScanLine, Trash2 } from 'lucide-react';
import { useAuth, useBusinessPath } from '../../lib/auth';
import { fetchBlob } from '../../lib/api';
import { useWorkingLocation } from '../../lib/locationContext';
import { dateTime } from '../../lib/format';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  Input,
  Modal,
  Select,
  Spinner,
  Textarea,
  cn,
} from '../../components/ui';
import { PageHeader } from '../../layouts/AppShell';
import {
  usePharmacy,
  usePharmacyUpload,
  usePharmacyWrite,
  type ListOf,
  type MedicineListItem,
  type PharmacySettings,
} from './data';
import { ErrorLine, RxBadge, StatusBadge, Table, Td, errorText } from './parts';
import { AllergyBanner, ClinicalNotice, PatientFormModal, type PatientRecord } from './PatientsPage';

const STATUSES = ['received', 'approved', 'partially_dispensed', 'dispensed', 'rejected', 'cancelled'] as const;
const STATUS_LABELS: Record<(typeof STATUSES)[number], string> = {
  received: 'Awaiting review',
  approved: 'Approved',
  partially_dispensed: 'Part dispensed',
  dispensed: 'Dispensed',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
};

interface PrescriptionRow {
  id: string;
  reference: string;
  branch_name: string;
  patient_name: string;
  received_at: string;
  dispensing_status: string;
  fulfilment: string;
  delivery_status: string;
  reviewed_by_name: string | null;
  items: number;
}

interface PrescriptionItem {
  id: string;
  productId: string;
  productName: string;
  rxClass: string;
  baseUnitName: string;
  prescribedText: string;
  prescribedQuantity: number | null;
  approvedQuantity: number | null;
  approvedDirections: string | null;
  dispensedQuantity: number;
  outstandingQuantity: number | null;
}

interface Prescription {
  id: string;
  reference: string;
  branchId: string;
  patientId: string;
  patientName: string;
  patientAllergies: string[];
  prescriberName: string | null;
  prescriberRegistration: string | null;
  issuedOn: string | null;
  receivedAt: string;
  source: string;
  dispensingStatus: string;
  fulfilment: string;
  deliveryStatus: string;
  reviewedByName: string | null;
  reviewerRegistration: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
  note: string | null;
  items: PrescriptionItem[];
  documents: Array<{ id: string; filename: string; mime_type: string; byte_size: number; created_at: string }>;
}

type Label =
  | { dispensingId: string; printable: false; reason: string }
  | {
      dispensingId: string;
      printable: true;
      pharmacy: { name: string; branch: string | null; address: string | null; phone: string | null };
      patientName: string;
      prescriptionReference: string;
      medicine: string;
      quantity: string;
      directions: string;
      batches: Array<{ batchNumber: string; expiryDate: string }>;
      dispensedBy: string;
      dispensedAt: string;
      approvedBy: string | null;
    };

// ── List ───────────────────────────────────────────────────────────────────

export function PrescriptionsPage(): ReactNode {
  const { can, business } = useAuth();
  const tz = business?.timezone ?? 'UTC';
  const navigate = useNavigate();
  const { branchId } = useWorkingLocation();
  const [status, setStatus] = useState<string>('received');
  const [creating, setCreating] = useState(false);
  const list = usePharmacy<ListOf<PrescriptionRow>>('/prescriptions', { status, branchId }, can('prescriptions:read'));

  if (!can('prescriptions:read')) {
    return <EmptyState title="Prescriptions are clinical records" description="Ask the owner for access if your role needs it." />;
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title="Prescriptions"
        description="Received, reviewed by a pharmacist, and dispensed — in full or in parts"
        action={
          can('prescriptions:manage') ? (
            <Button icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
              New prescription
            </Button>
          ) : undefined
        }
      />
      <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
        <div role="radiogroup" aria-label="Status" className="inline-flex gap-1 rounded-lg border border-slate-200 bg-white p-1">
          {STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              role="radio"
              aria-checked={status === s}
              onClick={() => setStatus(s)}
              className={cn(
                'whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium',
                status === s ? 'bg-brand-600 text-white' : 'text-slate-600 hover:bg-slate-50',
              )}
            >
              {STATUS_LABELS[s]}
            </button>
          ))}
        </div>
      </div>

      <Card padded={false}>
        {list.isError ? (
          <ErrorState message="Could not load prescriptions." onRetry={() => void list.refetch()} />
        ) : !list.data ? (
          <Spinner label="Loading prescriptions" />
        ) : list.data.data.length === 0 ? (
          <EmptyState icon={<FileText className="size-8" />} title={`Nothing ${STATUS_LABELS[status as keyof typeof STATUS_LABELS].toLowerCase()}`} />
        ) : (
          <Table head={['Reference', 'Patient', 'Received', 'Location', 'Items', 'Status', 'Reviewed by']} minWidth={760}>
            {list.data.data.map((r) => (
              <tr key={r.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/pharmacy/prescriptions/${r.id}`)}>
                <Td>
                  <Link to={`/pharmacy/prescriptions/${r.id}`} className="font-medium text-brand-700 hover:underline">
                    {r.reference}
                  </Link>
                </Td>
                <Td>{r.patient_name}</Td>
                <Td className="whitespace-nowrap">{dateTime(r.received_at, tz)}</Td>
                <Td>{r.branch_name}</Td>
                <Td className="tabular-nums">{r.items}</Td>
                <Td>
                  <StatusBadge status={r.dispensing_status} />
                  {r.fulfilment === 'delivery' && (
                    <span className="ml-1">
                      <Badge tone="info">delivery: {r.delivery_status.replace(/_/g, ' ')}</Badge>
                    </span>
                  )}
                </Td>
                <Td>{r.reviewed_by_name ?? '—'}</Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <NewPrescriptionModal
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(id) => {
          setCreating(false);
          navigate(`/pharmacy/prescriptions/${id}`);
        }}
      />
    </div>
  );
}

// ── New prescription ───────────────────────────────────────────────────────

interface DraftItem {
  productId: string;
  productName: string;
  rxClass: string;
  baseUnitName: string;
  prescribedText: string;
  prescribedQuantity: string;
}

function NewPrescriptionModal({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: (id: string) => void }): ReactNode {
  const { locations, branchId, defaultBranchId } = useWorkingLocation();
  const [location, setLocation] = useState(branchId ?? defaultBranchId ?? '');
  const [patient, setPatient] = useState<{ id: string; name: string } | null>(null);
  const [patientSearch, setPatientSearch] = useState('');
  const [newPatient, setNewPatient] = useState(false);
  const [prescriberId, setPrescriberId] = useState('');
  const [newPrescriber, setNewPrescriber] = useState({ fullName: '', registrationNumber: '', facility: '' });
  const [addingPrescriber, setAddingPrescriber] = useState(false);
  const [issuedOn, setIssuedOn] = useState('');
  const [source, setSource] = useState('paper');
  const [fulfilment, setFulfilment] = useState('pickup');
  const [note, setNote] = useState('');
  const [items, setItems] = useState<DraftItem[]>([]);

  const patients = usePharmacy<ListOf<{ id: string; full_name: string; phone: string | null }>>(
    '/patients',
    { search: patientSearch },
    open && patientSearch.trim().length >= 2,
  );
  const prescribers = usePharmacy<ListOf<{ id: string; full_name: string; registration_number: string | null; facility: string | null }>>('/prescribers', {}, open);
  const settings = usePharmacy<PharmacySettings>('/settings', {}, open);
  const createPrescriber = usePharmacyWrite<Record<string, unknown>, { id: string }>('post', '/prescribers');
  const create = usePharmacyWrite<Record<string, unknown>, { id: string }>('post', '/prescriptions');

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!patient) return;
    create.mutate(
      {
        branchId: location,
        patientId: patient.id,
        prescriberId: prescriberId || null,
        issuedOn: issuedOn || null,
        source,
        fulfilment,
        note: note || null,
        items: items.map((i) => ({
          productId: i.productId,
          prescribedText: i.prescribedText,
          prescribedQuantity: i.prescribedQuantity ? Number(i.prescribedQuantity) : null,
        })),
      },
      { onSuccess: (r) => onCreated(r.id) },
    );
  }

  const deliveryAllowed = settings.data?.deliveryEnabled === true;
  const valid = patient && location && items.length > 0 && items.every((i) => i.prescribedText.trim());

  return (
    <Modal open={open} onClose={onClose} title="New prescription" size="lg">
      <form onSubmit={submit} className="space-y-5">
        <ClinicalNotice />
        <Select
          label="Location"
          required
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder="Choose a location"
          options={locations.map((l) => ({ value: l.id, label: l.name }))}
        />

        <section>
          <p className="label-base">Patient</p>
          {patient ? (
            <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-sm">
              <span className="font-medium">{patient.name}</span>
              <Button type="button" size="sm" variant="ghost" onClick={() => setPatient(null)}>
                Change
              </Button>
            </div>
          ) : (
            <>
              <div className="flex gap-2">
                <Input aria-label="Search patients" placeholder="Name or phone" value={patientSearch} onChange={(e) => setPatientSearch(e.target.value)} />
                <Button type="button" variant="secondary" onClick={() => setNewPatient(true)}>
                  New
                </Button>
              </div>
              {(patients.data?.data ?? []).length > 0 && (
                <ul className="mt-2 divide-y divide-slate-100 rounded-lg border border-slate-200">
                  {patients.data!.data.map((p) => (
                    <li key={p.id}>
                      <button
                        type="button"
                        className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50"
                        onClick={() => setPatient({ id: p.id, name: p.full_name })}
                      >
                        {p.full_name} <span className="text-slate-500">{p.phone ?? ''}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </section>

        <section className="space-y-2">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Select
                label="Prescriber"
                value={prescriberId}
                onChange={(e) => setPrescriberId(e.target.value)}
                placeholder="Not recorded"
                options={(prescribers.data?.data ?? []).map((p) => ({
                  value: p.id,
                  label: [p.full_name, p.registration_number, p.facility].filter(Boolean).join(' · '),
                }))}
              />
            </div>
            <Button type="button" variant="secondary" onClick={() => setAddingPrescriber((v) => !v)}>
              {addingPrescriber ? 'Close' : 'Add'}
            </Button>
          </div>
          {addingPrescriber && (
            <div className="grid grid-cols-1 gap-2 rounded-lg border border-slate-200 p-3 sm:grid-cols-3">
              <Input label="Name" value={newPrescriber.fullName} onChange={(e) => setNewPrescriber({ ...newPrescriber, fullName: e.target.value })} />
              <Input label="Registration" value={newPrescriber.registrationNumber} onChange={(e) => setNewPrescriber({ ...newPrescriber, registrationNumber: e.target.value })} />
              <Input label="Facility" value={newPrescriber.facility} onChange={(e) => setNewPrescriber({ ...newPrescriber, facility: e.target.value })} />
              <div className="sm:col-span-3">
                <ErrorLine error={createPrescriber.error} />
                <Button
                  type="button"
                  size="sm"
                  loading={createPrescriber.isPending}
                  disabled={!newPrescriber.fullName.trim()}
                  onClick={() =>
                    createPrescriber.mutate(
                      {
                        fullName: newPrescriber.fullName,
                        registrationNumber: newPrescriber.registrationNumber || null,
                        facility: newPrescriber.facility || null,
                      },
                      {
                        onSuccess: (r) => {
                          setPrescriberId(r.id);
                          setAddingPrescriber(false);
                          setNewPrescriber({ fullName: '', registrationNumber: '', facility: '' });
                        },
                      },
                    )
                  }
                >
                  Save prescriber
                </Button>
              </div>
            </div>
          )}
        </section>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Input label="Issued on" type="date" value={issuedOn} onChange={(e) => setIssuedOn(e.target.value)} />
          <Select
            label="Received as"
            value={source}
            onChange={(e) => setSource(e.target.value)}
            options={[
              { value: 'paper', label: 'Paper' },
              { value: 'electronic', label: 'Electronic' },
              { value: 'verbal', label: 'Verbal' },
              { value: 'repeat', label: 'Repeat' },
            ]}
          />
          <Select
            label="Fulfilment"
            value={fulfilment}
            onChange={(e) => setFulfilment(e.target.value)}
            hint={deliveryAllowed ? undefined : 'Delivery is not enabled for this pharmacy'}
            options={[{ value: 'pickup', label: 'Pickup' }, ...(deliveryAllowed ? [{ value: 'delivery', label: 'Delivery' }] : [])]}
          />
        </div>

        <section className="space-y-2">
          <p className="label-base">Items, as written</p>
          {items.map((item, i) => (
            <div key={i} className="rounded-lg border border-slate-200 p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="text-sm font-medium">
                  {item.productName} <RxBadge rxClass={item.rxClass} />
                </span>
                <button type="button" aria-label="Remove item" className="text-slate-400 hover:text-red-600" onClick={() => setItems(items.filter((_, j) => j !== i))}>
                  <Trash2 className="size-4" />
                </button>
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                <div className="sm:col-span-2">
                  <Input
                    label="Prescribed (as written)"
                    required
                    value={item.prescribedText}
                    onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, prescribedText: e.target.value } : x)))}
                  />
                </div>
                <Input
                  label={`Quantity (${item.baseUnitName}s)`}
                  type="number"
                  min={1}
                  step={1}
                  value={item.prescribedQuantity}
                  onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, prescribedQuantity: e.target.value } : x)))}
                />
              </div>
            </div>
          ))}
          <MedicineSearch
            onPick={(m) =>
              setItems([
                ...items,
                { productId: m.id, productName: m.name, rxClass: m.rxClass, baseUnitName: m.baseUnitName, prescribedText: '', prescribedQuantity: '' },
              ])
            }
          />
        </section>

        <Textarea label="Note" value={note} onChange={(e) => setNote(e.target.value)} />
        <ErrorLine error={create.error} />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={create.isPending} disabled={!valid}>
            Record prescription
          </Button>
        </div>
      </form>
      <PatientFormModal
        open={newPatient}
        onClose={() => setNewPatient(false)}
        onSaved={(p: PatientRecord) => {
          setNewPatient(false);
          setPatient({ id: p.id, name: p.fullName });
        }}
      />
    </Modal>
  );
}

function MedicineSearch({ onPick }: { onPick: (m: MedicineListItem) => void }): ReactNode {
  const [search, setSearch] = useState('');
  const results = usePharmacy<ListOf<MedicineListItem>>('/medicines', { search, limit: 8 }, search.trim().length >= 2);
  return (
    <div>
      <Input aria-label="Add a medicine" placeholder="Add a medicine — name, generic or barcode" value={search} onChange={(e) => setSearch(e.target.value)} />
      {search.trim().length >= 2 && (results.data?.data ?? []).length > 0 && (
        <ul className="mt-1 divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white">
          {results.data!.data.map((m) => (
            <li key={m.id}>
              <button
                type="button"
                className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50"
                onClick={() => {
                  onPick(m);
                  setSearch('');
                }}
              >
                <span>
                  {m.name} <span className="text-slate-500">{[m.strength, m.dosageForm].filter(Boolean).join(' ')}</span>
                </span>
                <RxBadge rxClass={m.rxClass} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Detail ─────────────────────────────────────────────────────────────────

export function PrescriptionDetailPage(): ReactNode {
  const { id = '' } = useParams();
  const { can, business } = useAuth();
  const tz = business?.timezone ?? 'UTC';
  const navigate = useNavigate();
  const rx = usePharmacy<Prescription>(`/prescriptions/${id}`, {}, can('prescriptions:read'));
  const r = rx.data;

  return (
    <div className="space-y-5">
      <div className="print:hidden">
        <Link to="/pharmacy/prescriptions" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900">
          <ArrowLeft className="size-4" aria-hidden /> Prescriptions
        </Link>
      </div>
      {rx.isError ? (
        <ErrorState message="Could not load this prescription." onRetry={() => void rx.refetch()} />
      ) : !r ? (
        <Spinner label="Loading prescription" />
      ) : (
        <>
          <div className="space-y-5 print:hidden">
            <PageHeader
              title={r.reference}
              description={`Received ${dateTime(r.receivedAt, tz)} · ${r.source}`}
              action={
                can('prescriptions:dispense') && ['approved', 'partially_dispensed'].includes(r.dispensingStatus) ? (
                  <Button icon={<ScanLine className="size-4" />} onClick={() => navigate(`/pharmacy/pos?rx=${r.id}`)}>
                    Dispense at POS
                  </Button>
                ) : undefined
              }
            />
            <ClinicalNotice />

            <section aria-label="Statuses" className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="rounded-xl border border-slate-200 bg-white p-3.5">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Dispensing</p>
                <div className="mt-1.5">
                  <StatusBadge status={r.dispensingStatus} />
                </div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-3.5">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Payment</p>
                <p className="mt-1.5 text-sm text-slate-600">Recorded on the sale it is dispensed through — paying does not dispense, and dispensing does not mark it paid.</p>
              </div>
              <DeliveryStatusTile rx={r} />
            </section>

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
              <div className="space-y-3 lg:col-span-2">
                <Card>
                  <CardHeader title={r.patientName} subtitle="Patient" action={<Link to={`/pharmacy/patients/${r.patientId}`} className="text-sm text-brand-700 hover:underline">Open record</Link>} />
                  <AllergyBanner allergies={r.patientAllergies} />
                </Card>
              </div>
              <Card>
                <CardHeader title="Prescriber" />
                <p className="text-sm text-slate-900">{r.prescriberName ?? 'Not recorded'}</p>
                {r.prescriberRegistration && <p className="text-xs text-slate-500">Reg. {r.prescriberRegistration}</p>}
                {r.issuedOn && <p className="mt-1 text-xs text-slate-500">Issued {r.issuedOn}</p>}
                {r.note && <p className="mt-2 text-sm text-slate-600">{r.note}</p>}
              </Card>
            </div>

            <Card padded={false}>
              <div className="p-4 sm:p-5">
                <CardHeader
                  title="Items"
                  subtitle={
                    r.reviewedAt
                      ? `Reviewed by ${r.reviewedByName ?? '—'}${r.reviewerRegistration ? ` (reg. ${r.reviewerRegistration})` : ''} · ${dateTime(r.reviewedAt, tz)}`
                      : 'Not yet reviewed by a pharmacist'
                  }
                />
                {r.reviewNote && <p className="-mt-2 text-sm text-slate-600">Review note: {r.reviewNote}</p>}
              </div>
              <Table head={['Medicine', 'Prescribed (as written)', 'Approved', 'Directions approved', 'Dispensed', 'Outstanding']} minWidth={820}>
                {r.items.map((i) => (
                  <tr key={i.id}>
                    <Td className="font-medium">
                      {i.productName} <RxBadge rxClass={i.rxClass} />
                    </Td>
                    <Td className="max-w-[14rem]">
                      {i.prescribedText}
                      {i.prescribedQuantity !== null && <span className="block text-xs text-slate-500">{i.prescribedQuantity} {i.baseUnitName}s</span>}
                    </Td>
                    <Td className="tabular-nums">{i.approvedQuantity ?? '—'}</Td>
                    <Td className="max-w-[16rem]">{i.approvedDirections ?? '—'}</Td>
                    <Td className="tabular-nums">{i.dispensedQuantity}</Td>
                    <Td className="tabular-nums">{i.outstandingQuantity ?? '—'}</Td>
                  </tr>
                ))}
              </Table>
            </Card>

            {can('prescriptions:review') && r.dispensingStatus === 'received' && <ReviewForm rx={r} />}

            <Documents rx={r} />

            {can('prescriptions:review') && !['dispensed', 'cancelled'].includes(r.dispensingStatus) && <CancelPrescription id={r.id} />}
          </div>

          {can('prescriptions:dispense') && r.items.some((i) => i.dispensedQuantity > 0) && <Labels id={r.id} tz={tz} />}
        </>
      )}
    </div>
  );
}

function DeliveryStatusTile({ rx }: { rx: Prescription }): ReactNode {
  const { can } = useAuth();
  const write = usePharmacyWrite<{ deliveryStatus: string }>('post', `/prescriptions/${rx.id}/delivery-status`);
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3.5">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Delivery</p>
      {rx.fulfilment !== 'delivery' ? (
        <p className="mt-1.5 text-sm text-slate-600">Pickup at the pharmacy</p>
      ) : can('prescriptions:manage') ? (
        <>
          <Select
            aria-label="Delivery status"
            className="mt-1.5"
            value={rx.deliveryStatus}
            disabled={write.isPending}
            onChange={(e) => write.mutate({ deliveryStatus: e.target.value })}
            options={['awaiting', 'ready', 'out_for_delivery', 'delivered', 'failed'].map((s) => ({ value: s, label: s.replace(/_/g, ' ') }))}
          />
          <ErrorLine error={write.error} />
        </>
      ) : (
        <div className="mt-1.5">
          <StatusBadge status={rx.deliveryStatus} />
        </div>
      )}
    </div>
  );
}

function ReviewForm({ rx }: { rx: Prescription }): ReactNode {
  const review = usePharmacyWrite<Record<string, unknown>>('post', `/prescriptions/${rx.id}/review`);
  const [lines, setLines] = useState(() =>
    rx.items.map((i) => ({ itemId: i.id, approvedQuantity: i.prescribedQuantity ? String(i.prescribedQuantity) : '', approvedDirections: '' })),
  );
  const [note, setNote] = useState('');
  const complete = lines.every((l) => l.approvedQuantity !== '' && Number.isInteger(Number(l.approvedQuantity)) && l.approvedDirections.trim().length >= 3);

  function decide(decision: 'approve' | 'reject') {
    review.mutate({
      decision,
      note: note || null,
      items:
        decision === 'approve'
          ? lines.map((l) => ({ itemId: l.itemId, approvedQuantity: Number(l.approvedQuantity), approvedDirections: l.approvedDirections.trim() }))
          : [],
    });
  }

  return (
    <Card>
      <CardHeader title="Pharmacist review" subtitle="Your name and registration are recorded with the decision" />
      <p className="mb-4 rounded-lg border border-brand-200 bg-brand-50 px-3 py-2 text-sm text-brand-800">
        CenoX never writes or suggests clinical directions; type the directions you approve. They are printed on the
        label exactly as written.
      </p>
      <div className="space-y-3">
        {rx.items.map((item, i) => (
          <div key={item.id} className="rounded-lg border border-slate-200 p-3">
            <p className="text-sm font-medium">
              {item.productName} <span className="font-normal text-slate-500">— “{item.prescribedText}”</span>
            </p>
            <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-4">
              <Input
                label={`Approve (${item.baseUnitName}s)`}
                type="number"
                min={0}
                step={1}
                hint="0 declines this item"
                value={lines[i]!.approvedQuantity}
                onChange={(e) => setLines(lines.map((l, j) => (j === i ? { ...l, approvedQuantity: e.target.value } : l)))}
              />
              <div className="sm:col-span-3">
                <Textarea
                  label="Directions for the label"
                  rows={2}
                  value={lines[i]!.approvedDirections}
                  onChange={(e) => setLines(lines.map((l, j) => (j === i ? { ...l, approvedDirections: e.target.value } : l)))}
                />
              </div>
            </div>
          </div>
        ))}
        <Textarea label="Review note" value={note} onChange={(e) => setNote(e.target.value)} />
        <ErrorLine error={review.error} />
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="danger" loading={review.isPending} onClick={() => decide('reject')}>
            Reject
          </Button>
          <Button loading={review.isPending} disabled={!complete} onClick={() => decide('approve')}>
            Approve
          </Button>
        </div>
      </div>
    </Card>
  );
}

function Documents({ rx }: { rx: Prescription }): ReactNode {
  const { can } = useAuth();
  const path = useBusinessPath();
  const upload = usePharmacyUpload((id) => `/prescriptions/${id}/documents`);
  const [viewError, setViewError] = useState<unknown>(null);

  async function view(documentId: string) {
    setViewError(null);
    // A short-lived object URL: the file never gets an address of its own.
    const tab = window.open('', '_blank');
    try {
      const blob = await fetchBlob(path(`/pharmacy/prescriptions/${rx.id}/documents/${documentId}`));
      const url = URL.createObjectURL(blob);
      if (tab) tab.location.href = url;
      else window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      tab?.close();
      setViewError(error);
    }
  }

  return (
    <Card>
      <CardHeader
        title="Documents"
        subtitle="Prescription scans and photos — served only to staff who may read prescriptions"
        action={
          can('prescriptions:manage') ? (
            <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
              <Paperclip className="size-4" aria-hidden /> {upload.isPending ? 'Uploading…' : 'Attach'}
              <input
                type="file"
                className="sr-only"
                accept="image/jpeg,image/png,image/webp,image/heic,application/pdf"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const form = new FormData();
                  form.append('file', file);
                  upload.mutate({ id: rx.id, form });
                  e.target.value = '';
                }}
              />
            </label>
          ) : undefined
        }
      />
      {rx.documents.length === 0 ? (
        <p className="text-sm text-slate-500">No documents attached.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {rx.documents.map((d) => (
            <li key={d.id} className="flex items-center justify-between gap-2 py-2 text-sm">
              <span className="truncate">{d.filename}</span>
              <Button size="sm" variant="secondary" onClick={() => void view(d.id)}>
                View
              </Button>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-2 space-y-2">
        <ErrorLine error={upload.error} />
        <ErrorLine error={viewError} />
      </div>
    </Card>
  );
}

function CancelPrescription({ id }: { id: string }): ReactNode {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const cancel = usePharmacyWrite<{ note: string }>('post', `/prescriptions/${id}/cancel`);
  return (
    <>
      <div className="flex justify-end">
        <Button variant="ghost" onClick={() => setOpen(true)}>
          Cancel prescription
        </Button>
      </div>
      <Modal open={open} onClose={() => setOpen(false)} title="Cancel prescription">
        <div className="space-y-3">
          <Textarea label="Reason" required value={note} onChange={(e) => setNote(e.target.value)} />
          <ErrorLine error={cancel.error} />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Keep
            </Button>
            <Button variant="danger" loading={cancel.isPending} disabled={note.trim().length < 3} onClick={() => cancel.mutate({ note }, { onSuccess: () => setOpen(false) })}>
              Cancel prescription
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}

function Labels({ id, tz }: { id: string; tz: string }): ReactNode {
  const labels = usePharmacy<ListOf<Label>>(`/prescriptions/${id}/labels`);
  return (
    <section aria-label="Labels" className="space-y-3">
      <style>{`@media print { @page { margin: 8mm; } body * { visibility: hidden; } .rx-labels, .rx-labels * { visibility: visible; } .rx-labels { position: absolute; inset: 0; } }`}</style>
      <div className="flex items-center justify-between print:hidden">
        <h2 className="text-base font-semibold text-slate-900">Labels</h2>
        <Button variant="secondary" icon={<Printer className="size-4" />} onClick={() => window.print()}>
          Print labels
        </Button>
      </div>
      {labels.isError ? (
        <p className="text-sm text-red-700">{errorText(labels.error)}</p>
      ) : !labels.data ? (
        <Spinner label="Loading labels" />
      ) : (
        <div className="rx-labels grid grid-cols-1 gap-3 sm:grid-cols-2">
          {labels.data.data.map((l) =>
            l.printable ? (
              <article key={l.dispensingId} className="break-inside-avoid rounded-lg border-2 border-slate-800 bg-white p-3 text-sm text-slate-900">
                <p className="font-semibold">{l.pharmacy.name}</p>
                <p className="text-xs">{[l.pharmacy.branch, l.pharmacy.address, l.pharmacy.phone].filter(Boolean).join(' · ')}</p>
                <hr className="my-2 border-slate-300" />
                <p className="text-base font-semibold">{l.medicine}</p>
                <p>Qty: {l.quantity}</p>
                <p className="my-2 text-base font-medium">{l.directions}</p>
                <p>Patient: <span className="font-medium">{l.patientName}</span></p>
                <p className="text-xs">
                  {l.batches.map((b) => `Batch ${b.batchNumber} · exp ${b.expiryDate}`).join('; ')}
                </p>
                <p className="mt-1 text-xs">
                  {l.prescriptionReference} · Dispensed by {l.dispensedBy}
                  {l.approvedBy ? ` · Approved by ${l.approvedBy}` : ''} · {dateTime(l.dispensedAt, tz)}
                </p>
              </article>
            ) : (
              <div key={l.dispensingId} className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 print:hidden">
                No label: {l.reason}
              </div>
            ),
          )}
        </div>
      )}
    </section>
  );
}
